import { useState, useEffect, useMemo } from "react";
import {
  Plus, ChevronRight, ChevronLeft, Zap, Search,
  Calendar, CheckCircle2, Sparkles, X, SlidersHorizontal,
  BookOpen, ClipboardList, Trophy, Dumbbell, Microscope,
  Presentation, Users, Wrench, Heart, LibraryBig, MoreHorizontal,
  Meh, Frown, Smile, Star, PartyPopper,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Sidebar } from "@/components/ui/sidebar";
import ProtectedRoute from "@/components/ProtectedRoute";
import { useTraitMutation } from "@/hooks/useTraitMutation";
import {
  ActivityLogType, OutcomeRating,
  ACTIVITY_LOG_META, OUTCOME_META,
  ActivityLogEntry,
} from "@/types/traitEvents";
import { StorageService } from "@/hooks/services/storageService";
import { AuthStore } from "@/hooks/stores/AuthStore";
import traitList from "../../../traitList.json";

const TRAIT_NAME: Record<string, string> = Object.fromEntries(
  (traitList as Array<{ id: string; full_name: string }>).map((t) => [t.id, t.full_name])
);

// Light-theme palette per activity type — static full class strings
const LIGHT_PALETTE: Record<ActivityLogType, { bg: string; text: string; border: string; iconBg: string }> = {
  exam:          { bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200",    iconBg: "bg-blue-100" },
  test:          { bg: "bg-indigo-50",  text: "text-indigo-700",  border: "border-indigo-200",  iconBg: "bg-indigo-100" },
  sport:         { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", iconBg: "bg-emerald-100" },
  competition:   { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   iconBg: "bg-amber-100" },
  project:       { bg: "bg-orange-50",  text: "text-orange-700",  border: "border-orange-200",  iconBg: "bg-orange-100" },
  presentation:  { bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-200",    iconBg: "bg-rose-100" },
  club:          { bg: "bg-violet-50",  text: "text-violet-700",  border: "border-violet-200",  iconBg: "bg-violet-100" },
  workshop:      { bg: "bg-cyan-50",    text: "text-cyan-700",    border: "border-cyan-200",    iconBg: "bg-cyan-100" },
  volunteer:     { bg: "bg-teal-50",    text: "text-teal-700",    border: "border-teal-200",    iconBg: "bg-teal-100" },
  study_session: { bg: "bg-slate-50",   text: "text-slate-700",   border: "border-slate-200",   iconBg: "bg-slate-100" },
  other:         { bg: "bg-pink-50",    text: "text-pink-700",    border: "border-pink-200",    iconBg: "bg-pink-100" },
};

// Lucide icon map — one per activity type (emojis are fallback only)
const ACTIVITY_ICONS: Record<ActivityLogType, LucideIcon> = {
  exam:          ClipboardList,
  test:          BookOpen,
  sport:         Dumbbell,
  competition:   Trophy,
  project:       Microscope,
  presentation:  Presentation,
  club:          Users,
  workshop:      Wrench,
  volunteer:     Heart,
  study_session: LibraryBig,
  other:         MoreHorizontal,
};

// Outcome icons
const OUTCOME_ICONS: Record<number, LucideIcon> = {
  1: Frown,
  2: Frown,
  3: Meh,
  4: Smile,
  5: Star,
};

const FALLBACK_META = { label: "Activity", icon: "", coreTraits: [], color: "" };
const FALLBACK_ICON: LucideIcon = Sparkles;
const FALLBACK_PALETTE = { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200", iconBg: "bg-slate-100" };
const FALLBACK_OUTCOME = { label: "Completed", emoji: "✅", multiplier: 1 };

// ─── Activity Card ────────────────────────────────────────────────────────────
function ActivityCard({ entry }: { entry: ActivityLogEntry }) {
  const meta    = ACTIVITY_LOG_META[entry.activityType] ?? FALLBACK_META;
  const pal     = LIGHT_PALETTE[entry.activityType] ?? FALLBACK_PALETTE;
  const outcome = OUTCOME_META[entry.outcomeRating] ?? FALLBACK_OUTCOME;
  const Icon    = ACTIVITY_ICONS[entry.activityType] ?? FALLBACK_ICON;
  const OutIcon = OUTCOME_ICONS[entry.outcomeRating] ?? Meh;
  const dateStr = new Date(entry.timestamp).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });

  return (
    <div className={`bg-white border ${pal.border} rounded-2xl p-4 shadow-sm flex flex-col gap-3 hover:shadow-md transition-shadow`}>
      {/* Top row */}
      <div className="flex items-start gap-3">
        <div className={`${pal.iconBg} w-10 h-10 rounded-xl flex items-center justify-center shrink-0`}>
          <Icon className={`w-5 h-5 ${pal.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900 leading-tight">
            {meta.label}
            {entry.subject ? <span className="text-slate-500 font-medium"> — {entry.subject}</span> : null}
          </p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <span className={`text-[11px] font-semibold ${pal.text} ${pal.bg} px-2 py-0.5 rounded-full`}>
              {meta.label}
            </span>
            {entry.score !== undefined && (
              <span className="text-[11px] font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                {entry.score}%
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Outcome + date */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <OutIcon className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs text-slate-500">{outcome.label}</span>
        </div>
        <div className="flex items-center gap-1 text-slate-400 text-[11px]">
          <Calendar className="w-3 h-3" />
          {dateStr}
        </div>
      </div>

      {/* Note preview */}
      {entry.note && (
        <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2 border-t border-slate-100 pt-2">
          {entry.note}
        </p>
      )}
    </div>
  );
}

// ─── Step progress dots ───────────────────────────────────────────────────────
function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all ${
            i < current ? "bg-indigo-500 w-6" : i === current ? "bg-indigo-400 w-4" : "bg-slate-200 w-3"
          }`}
        />
      ))}
    </div>
  );
}

// ─── Subjects ─────────────────────────────────────────────────────────────────
const SUBJECTS = [
  "Mathematics", "English Language", "Physics", "Chemistry", "Biology",
  "Economics", "Government", "History", "Geography", "Literature",
  "Computer Studies", "Agricultural Science", "Further Mathematics",
  "Yoruba", "Igbo", "Hausa", "French", "Fine Art", "Music",
  "Physical Education", "Technical Drawing", "Other",
];

// ─── Log Activity Modal ───────────────────────────────────────────────────────
function LogModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (traits: string[], type: ActivityLogType) => void;
}) {
  const { emitActivityLog } = useTraitMutation();
  const [step, setStep] = useState(0);
  const [activityType, setActivityType] = useState<ActivityLogType | null>(null);
  const [subject, setSubject] = useState("");
  const [score, setScore] = useState("");
  const [outcome, setOutcome] = useState<OutcomeRating | null>(null);
  const [note, setNote] = useState("");

  const TOTAL = 4;
  const canProceed = step === 0 ? !!activityType : step === 1 ? !!subject : step === 2 ? outcome !== null : true;

  const handleNext = () => {
    if (step < TOTAL - 1) { setStep((s) => s + 1); return; }
    if (!activityType || !outcome) return;
    const numScore = score ? parseInt(score) : undefined;
    emitActivityLog(activityType, subject, outcome, numScore, note);
    const traits = ACTIVITY_LOG_META[activityType]?.coreTraits ?? [];
    onSaved(traits, activityType);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <StepDots current={step} total={TOTAL} />
            <span className="text-xs text-slate-400 font-medium">Step {step + 1} of {TOTAL}</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Step 0 — Activity type */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-black text-slate-900">What happened?</h2>
                <p className="text-slate-500 text-sm mt-0.5">Pick the type of activity</p>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {(Object.entries(ACTIVITY_LOG_META) as [ActivityLogType, typeof ACTIVITY_LOG_META[ActivityLogType]][]).map(([type, meta]) => {
                  const pal = LIGHT_PALETTE[type] ?? FALLBACK_PALETTE;
                  const TypeIcon = ACTIVITY_ICONS[type] ?? FALLBACK_ICON;
                  const isSelected = activityType === type;
                  return (
                    <button
                      key={type}
                      onClick={() => setActivityType(type)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 transition-all ${
                        isSelected
                          ? `${pal.border} ${pal.bg} shadow-sm scale-[1.03]`
                          : "border-slate-100 bg-white hover:border-slate-200"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? pal.iconBg : "bg-slate-100"}`}>
                        <TypeIcon className={`w-4 h-4 ${isSelected ? pal.text : "text-slate-500"}`} />
                      </div>
                      <span className={`text-[10px] font-semibold text-center leading-tight ${isSelected ? pal.text : "text-slate-500"}`}>
                        {meta.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 1 — Subject + score */}
          {step === 1 && activityType && (() => {
            const S1Icon = ACTIVITY_ICONS[activityType] ?? FALLBACK_ICON;
            const s1Pal = LIGHT_PALETTE[activityType] ?? FALLBACK_PALETTE;
            return (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${s1Pal.iconBg}`}>
                  <S1Icon className={`w-5 h-5 ${s1Pal.text}`} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-slate-900">{ACTIVITY_LOG_META[activityType].label}</h2>
                  <p className="text-slate-500 text-sm">Tell us a bit more</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-700">Subject / Area</label>
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                >
                  <option value="">Select a subject...</option>
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {["exam", "test", "competition"].includes(activityType) && (
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-700">
                    Score <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number" min="0" max="100" value={score}
                      onChange={(e) => setScore(e.target.value)}
                      placeholder="e.g. 78"
                      className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 placeholder-slate-400"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">/ 100</span>
                  </div>
                </div>
              )}
            </div>
          );})()}

          {/* Step 2 — Outcome */}
          {step === 2 && activityType && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-black text-slate-900">How did it go?</h2>
                <p className="text-slate-500 text-sm">{ACTIVITY_LOG_META[activityType].label}{subject ? ` — ${subject}` : ""}</p>
              </div>
              <div className="space-y-2">
                {([1, 2, 3, 4, 5] as OutcomeRating[]).map((rating) => {
                  const om = OUTCOME_META[rating];
                  const OIcon = OUTCOME_ICONS[rating] ?? Meh;
                  const isSelected = outcome === rating;
                  return (
                    <button
                      key={rating}
                      onClick={() => setOutcome(rating)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                        isSelected
                          ? "border-indigo-400 bg-indigo-50"
                          : "border-slate-100 bg-white hover:border-slate-200"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? "bg-indigo-100" : "bg-slate-100"}`}>
                        <OIcon className={`w-4 h-4 ${isSelected ? "text-indigo-600" : "text-slate-500"}`} />
                      </div>
                      <span className={`text-sm font-semibold ${isSelected ? "text-indigo-700" : "text-slate-700"}`}>
                        {om.label}
                      </span>
                      {isSelected && <CheckCircle2 className="w-4 h-4 text-indigo-500 ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3 — Note */}
          {step === 3 && (
            <div className="space-y-3">
              <div>
                <h2 className="text-xl font-black text-slate-900">Anything to add?</h2>
                <p className="text-slate-500 text-sm">Optional — a quick reflection or note.</p>
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 300))}
                placeholder="e.g. I struggled with the essay section but felt strong on comprehension..."
                rows={4}
                className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 placeholder-slate-400 resize-none"
              />
              <p className="text-slate-400 text-xs text-right">{note.length}/300</p>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={() => { if (step > 0) setStep((s) => s - 1); else onClose(); }}
            className="flex items-center gap-1.5 px-4 py-2.5 border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 rounded-xl text-sm font-semibold transition-all"
          >
            <ChevronLeft className="w-4 h-4" />
            {step === 0 ? "Cancel" : "Back"}
          </button>
          <button
            onClick={handleNext}
            disabled={!canProceed}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white font-bold rounded-xl text-sm transition-all active:scale-[0.98]"
          >
            {step === TOTAL - 1
              ? <><Sparkles className="w-4 h-4" /> Save Activity</>
              : <>Next <ChevronRight className="w-4 h-4" /></>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Boost Feedback Modal ─────────────────────────────────────────────────────
function BoostModal({ traits, activityType, onClose }: { traits: string[]; activityType: ActivityLogType; onClose: () => void }) {
  const meta = ACTIVITY_LOG_META[activityType] ?? FALLBACK_META;
  const TypeIcon = ACTIVITY_ICONS[activityType] ?? FALLBACK_ICON;
  const pal = LIGHT_PALETTE[activityType] ?? FALLBACK_PALETTE;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 text-center space-y-5">
        <div className={`w-16 h-16 rounded-2xl ${pal.iconBg} border ${pal.border} flex items-center justify-center mx-auto`}>
          <TypeIcon className={`w-8 h-8 ${pal.text}`} />
        </div>
        <div>
          <h3 className="text-xl font-black text-slate-900">Activity Logged!</h3>
          <p className="text-slate-500 text-sm mt-1">Your {meta.label.toLowerCase()} has updated your profile</p>
        </div>
        {traits.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center justify-center gap-1.5">
              <Zap className="w-3 h-3 text-amber-500" /> Traits Updated
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {traits.map((t) => (
                <span key={t} className="text-xs bg-indigo-50 border border-indigo-100 text-indigo-700 px-3 py-1 rounded-full font-semibold">
                  + {TRAIT_NAME[t] ?? t}
                </span>
              ))}
            </div>
          </div>
        )}
        <button onClick={onClose} className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all active:scale-[0.98]">
          View Activity Log
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const ALL_TYPES = Object.keys(ACTIVITY_LOG_META) as ActivityLogType[];

export default function ActivityLogger() {
  const { emitActivityLog } = useTraitMutation();

  const [feed, setFeed] = useState<ActivityLogEntry[]>([]);
  const [showLog, setShowLog] = useState(false);
  const [boostInfo, setBoostInfo] = useState<{ traits: string[]; type: ActivityLogType } | null>(null);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<ActivityLogType | "all">("all");

  const loadFeed = () => {
    const currentUser = AuthStore.getState().currentUser;
    if (!currentUser) return;
    const students: any[] = StorageService.read("students");
    const student = students.find((s) => s.userId === currentUser._id || s.email === currentUser.email);
    if (student?.activityLog) setFeed([...student.activityLog].reverse());
  };

  useEffect(() => { loadFeed(); }, []);

  const filtered = useMemo(() => {
    return feed.filter((e) => {
      const meta = ACTIVITY_LOG_META[e.activityType];
      if (filterType !== "all" && e.activityType !== filterType) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const inSubject = e.subject?.toLowerCase().includes(q);
        const inNote = e.note?.toLowerCase().includes(q);
        const inType = meta?.label.toLowerCase().includes(q);
        if (!inSubject && !inNote && !inType) return false;
      }
      return true;
    });
  }, [feed, filterType, search]);

  // Quick stats
  const thisWeek = useMemo(() => {
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return feed.filter((e) => new Date(e.timestamp).getTime() > weekAgo).length;
  }, [feed]);

  return (
    <ProtectedRoute>
      {showLog && (
        <LogModal
          onClose={() => setShowLog(false)}
          onSaved={(traits, type) => {
            setShowLog(false);
            setBoostInfo({ traits, type });
            loadFeed();
          }}
        />
      )}
      {boostInfo && (
        <BoostModal
          traits={boostInfo.traits}
          activityType={boostInfo.type}
          onClose={() => setBoostInfo(null)}
        />
      )}

      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />

        <main className="flex-1 lg:ml-64 px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto w-full">
          {/* Page header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-black text-slate-900">Activity Log</h1>
              <p className="text-slate-500 text-sm mt-0.5">Every real-world activity shapes your career profile</p>
            </div>
            <button
              onClick={() => setShowLog(true)}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-4 py-2.5 rounded-xl shadow-sm transition-all active:scale-[0.98] text-sm"
            >
              <Plus className="w-4 h-4" /> Log Activity
            </button>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: "Total logged", value: feed.length },
              { label: "This week", value: thisWeek },
              { label: "Activity types", value: new Set(feed.map((e) => e.activityType)).size },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white border border-slate-100 rounded-2xl px-4 py-3 shadow-sm text-center">
                <p className="text-xl font-black text-slate-900">{value}</p>
                <p className="text-xs text-slate-500 font-medium mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* Search + filter bar */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-4 mb-5 space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by subject, type or note..."
                className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>

            {/* Type filter chips */}
            <div className="flex items-center gap-2 flex-wrap">
              <SlidersHorizontal className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <button
                onClick={() => setFilterType("all")}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${
                  filterType === "all"
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                }`}
              >
                All
              </button>
              {ALL_TYPES.map((type) => {
                const meta = ACTIVITY_LOG_META[type];
                const pal = LIGHT_PALETTE[type];
                const FilterIcon = ACTIVITY_ICONS[type] ?? FALLBACK_ICON;
                const active = filterType === type;
                return (
                  <button
                    key={type}
                    onClick={() => setFilterType(active ? "all" : type)}
                    className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${
                      active
                        ? `${pal.bg} ${pal.text} ${pal.border}`
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <FilterIcon className="w-3 h-3" />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Feed grid */}
          {filtered.length === 0 ? (
            <div className="text-center py-20 space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center mx-auto">
                <Calendar className="w-7 h-7 text-slate-400" />
              </div>
              <div>
                <p className="text-slate-700 font-bold">
                  {feed.length === 0 ? "No activities yet" : "No results found"}
                </p>
                <p className="text-slate-500 text-sm mt-1">
                  {feed.length === 0
                    ? "Log your exams, sports, clubs and more — every activity refines your career profile."
                    : "Try adjusting your search or filters."}
                </p>
              </div>
              {feed.length === 0 && (
                <button
                  onClick={() => setShowLog(true)}
                  className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-3 rounded-xl transition-all text-sm"
                >
                  <Plus className="w-4 h-4" /> Log First Activity
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-400 font-semibold mb-3 uppercase tracking-wide">
                {filtered.length} {filtered.length === 1 ? "activity" : "activities"}
                {filterType !== "all" || search ? " · filtered" : ""}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map((entry) => (
                  <ActivityCard key={entry.id} entry={entry} />
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </ProtectedRoute>
  );
}
